import { VisitStatusId } from './visit.interface'

import type { IDataInstrument } from './visit.interface'
import type { IEntry } from '../instrument/instrument.interface'

const visitStatusLabel: { [key in VisitStatusId]: string } = {
  [VisitStatusId.Ordered]: 'Ordered',
  [VisitStatusId.Cancelled]: 'Cancelled',
  [VisitStatusId.Completed]: 'Completed',
  [VisitStatusId.InProgress]: 'In Progress',
  [VisitStatusId.NotStarted]: 'Not Started',
}

export const getVisitStatusLabel = (visitStatus: VisitStatusId) =>
  visitStatusLabel[visitStatus] ?? '–'

/**
 * Describes the action which launches the intake process.  This is used to
 * both identify whether or not the intake process can even start (`show`), and
 * what the `label` for the action should be given the status of the visit.
 */
type VisitIntakeStartAction =
  | { show: false }
  | {
      show: true
      label: string
      buttonType:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'danger'
        | 'warning'
        | 'link'
        | 'plain'
        | 'control'
    }

const visitIntakeStatusToStartAction: {
  [key in VisitStatusId]: VisitIntakeStartAction
} = {
  [VisitStatusId.Cancelled]: { show: false },
  [VisitStatusId.Completed]: {
    show: true,
    label: 'Amend',
    buttonType: 'secondary',
  },
  [VisitStatusId.InProgress]: {
    show: true,
    label: 'Resume',
    buttonType: 'primary',
  },
  [VisitStatusId.NotStarted]: {
    show: true,
    label: 'Start',
    buttonType: 'primary',
  },
  [VisitStatusId.Ordered]: {
    show: true,
    label: 'Start',
    buttonType: 'primary',
  },
}

export const getVisitIntakeStartAction = (visitStatus: VisitStatusId) =>
  visitIntakeStatusToStartAction[visitStatus]

const visitIntakeStartActionLabelPresentTense: {
  [key in VisitStatusId]: null | string
} = {
  [VisitStatusId.Cancelled]: null,
  [VisitStatusId.Completed]: 'Amending Patient Survey',
  [VisitStatusId.InProgress]: 'Resuming Patient Survey',
  [VisitStatusId.NotStarted]: 'Starting Patient Survey',
  [VisitStatusId.Ordered]: 'Starting Patient Survey',
}

export const getVisitIntakeStartActionLabelPresentTense = (
  visitStatus: VisitStatusId,
) => visitIntakeStartActionLabelPresentTense[visitStatus]

export type ExtractedPdfInfo = {
  id: string
  label: string
  isConsent: boolean
  isDisabled: boolean
  entryId: string
}

export const getVisitInstrumentPdfs = (
  visitInstruments: IDataInstrument[],
  entries: Record<string, IEntry>,
) => {
  const extractedPdfInfo: ExtractedPdfInfo[] = []

  visitInstruments.forEach((instrument) => {
    const isConsent = instrument.type === 'consent'

    instrument.pdfTemplates.forEach((template) => {
      // Find the matching IEntry for the PdfTemplate's instrumentId
      const matchingEntry = entries[instrument.id]
      if (matchingEntry) {
        let isDisabled = false
        if (isConsent) {
          // FIXME: Come up with a better solution for this.
          // ISSUE: https://cohesic.aha.io/develop/features/TEAMIDI-34
          // This logic is pre-existing, and we need to update the domain model to create a better solution.
          // The problem is we're determining whether to allow the downloading of a form based on the response
          // to a question on the consent instrument.
          // The problems are we assume there is only one important question, that question is the first question
          // on the form, and that the value of the response will be '1'.
          // With how configurable our instruments are, this may not be true in the future.
          // We likely want to create a field on a consent instrument that has logic to evaluate.
          // For example `consentProvidedLogic='[ciroc.consent_ciroc] == "1"'`
          const response = matchingEntry
            ? Object.values(matchingEntry.responses)[0]
            : false
          isDisabled = isDisabled = !response || response === '0'
        }

        extractedPdfInfo.push({
          id: template.id,
          label: template.label,
          isConsent: isConsent,
          entryId: matchingEntry.id,
          isDisabled: isDisabled,
        })
      }
    })
  })

  return extractedPdfInfo
}
